import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as QRCode from 'qrcode';
import apiService from '../services/apiService';
import { API_BASE_URL } from '../config/supabase';
import { getCurrentUser } from '../utils/auth';
import LanguageSwitcher from '../components/LanguageSwitcher';

interface PaymentProcessingPageProps {
  paymentData?: any;
  onPaymentComplete?: (result: any) => void;
}

const PaymentProcessingPage: React.FC<PaymentProcessingPageProps> = ({ 
  paymentData: propPaymentData, 
  onPaymentComplete 
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get payment data from navigation state or props
  const paymentData = location.state || propPaymentData;
  
  console.log('🔍 PaymentProcessingPage - location.state:', location.state);
  console.log('🔍 PaymentProcessingPage - paymentData:', paymentData);
  
  const [status, setStatus] = useState<'processing' | 'success' | 'failed'>('processing');
  // const [loading, setLoading] = useState(true); // Loading state not used in UI
  const [error, setError] = useState('');
  const [paymentResponse, setPaymentResponse] = useState<any>(null);
  const [timeRemaining, setTimeRemaining] = useState(900); // 15 minutes in seconds
  const qrCodeRef = useRef<HTMLCanvasElement>(null);
  const statusCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isStatusCheckActiveRef = useRef<boolean>(false);
  const isApiCallInProgressRef = useRef<boolean>(false);
  const currentTransactionIdRef = useRef<string | null>(null);
  const hasCreatedOrderRef = useRef<boolean>(false);

  // Manual stop function for status checking (currently unused but kept for future use)
  // const stopPaymentStatusCheck = () => {
    // console.log('🛑 Manually stopping payment status check');
    // isStatusCheckActiveRef.current = false;
    // if (statusCheckIntervalRef.current) {
    //   clearInterval(statusCheckIntervalRef.current);
    //   statusCheckIntervalRef.current = null;
    //   console.log('🧹 Status check interval manually cleared');
    // }
  // };

  // Generate QR code function
  const generateQRCode = async (paymentLink: string) => {
    if (qrCodeRef.current && paymentLink) {
      try {
        // Get the canvas size based on screen size
        const canvas = qrCodeRef.current;
        const size = window.innerWidth < 640 ? 128 : window.innerWidth < 768 ? 160 : 200;
        
        await QRCode.toCanvas(canvas, paymentLink, {
          width: size,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        console.log('✅ QR code generated successfully');
      } catch (error) {
        console.error('❌ Error generating QR code:', error);
      }
    }
  };

  // Payment status checking function
  const startPaymentStatusCheck = useCallback((transactionId: string) => {
    console.log('🚀 startPaymentStatusCheck function called with transactionId:', transactionId);
    console.log('🚀 Function execution started at:', new Date().toISOString());
    console.log('🚀 Function parameters:', { transactionId, type: typeof transactionId });
    console.log('🚀 Function is being executed in component:', 'PaymentProcessingPage');
    
    if (!transactionId) {
      console.error('❌ Cannot start payment status check: transactionId is undefined');
      return;
    }
    
    // Store transaction ID in ref for use in interval
    currentTransactionIdRef.current = transactionId;
    console.log('🚀 Transaction ID stored in ref:', currentTransactionIdRef.current);
    console.log('🚀 Ref state after storing:', {
      currentTransactionIdRef: currentTransactionIdRef.current,
      isStatusCheckActiveRef: isStatusCheckActiveRef.current
    });
    
    // Prevent multiple status checks from running
    if (isStatusCheckActiveRef.current) {
      console.log('🛑 Status check already active, skipping new one');
      return;
    }
    
    // Clear any existing interval first
    if (statusCheckIntervalRef.current) {
      console.log('🧹 Clearing existing status check interval before starting new one');
      clearInterval(statusCheckIntervalRef.current);
      statusCheckIntervalRef.current = null;
    }
    
    console.log('🔍 Starting payment status check for transaction ID:', transactionId);
    console.log('🔍 Setting isStatusCheckActiveRef to true');
    isStatusCheckActiveRef.current = true;
    console.log('🔍 isStatusCheckActiveRef set to:', isStatusCheckActiveRef.current);
    
    const checkStatus = async () => {
      console.log('🔄 checkStatus function called at:', new Date().toISOString());
      
      // Check if status check is still active
      if (!isStatusCheckActiveRef.current || !statusCheckIntervalRef.current) {
        console.log('🛑 Status check was deactivated, stopping API call');
        console.log('🛑 isStatusCheckActiveRef.current:', isStatusCheckActiveRef.current);
        console.log('🛑 statusCheckIntervalRef.current:', statusCheckIntervalRef.current);
        return;
      }
      
      // Get current transaction ID from ref
      const currentTransactionId = currentTransactionIdRef.current;
      if (!currentTransactionId) {
        console.error('❌ No transaction ID available for status check');
        console.error('❌ currentTransactionIdRef.current:', currentTransactionIdRef.current);
        return;
      }
      
      console.log('🔄 Payment status check running...', new Date().toISOString());
      console.log('🔄 Using transaction ID:', currentTransactionId);
      
      try {
        console.log('🔍 Checking payment status for transaction:', currentTransactionId);
        // Use the new public status endpoint
        const timestamp = Date.now();
        const statusUrl = `/transactions/status/${currentTransactionId}?t=${timestamp}`;
        console.log('🔍 Status check API URL:', statusUrl);
        const response = await apiService.get(statusUrl);
        console.log('🔍 Payment status response:', response);
        
        if (response.success) {
          const transactionStatus = response.data?.status || response.status;
          console.log('🔍 Transaction status:', transactionStatus);
          
          if (transactionStatus === 'completed' || transactionStatus === 'success') {
            console.log('✅ Payment completed successfully, stopping status checks');
            setStatus('success');
            isStatusCheckActiveRef.current = false;
            currentTransactionIdRef.current = null;
            // Clear the interval immediately when payment is successful
            if (statusCheckIntervalRef.current) {
              clearInterval(statusCheckIntervalRef.current);
              statusCheckIntervalRef.current = null;
              console.log('🧹 Status check interval cleared on success');
            }
            // Navigate to success page
            setTimeout(() => {
              navigate('/payment-success', { 
                state: { 
                  paymentData: {
                    ...paymentData,
                    paymentResponse: paymentResponse,
                    transactionId: transactionId
                  }
                } 
              });
            }, 2000);
            return; // Exit the function to prevent further checks
          } else if (transactionStatus === 'failed' || transactionStatus === 'error') {
            console.log('❌ Payment failed, stopping status checks');
            setStatus('failed');
            setError('Payment failed. Please try again.');
            isStatusCheckActiveRef.current = false;
            currentTransactionIdRef.current = null;
            // Clear the interval immediately when payment fails
            if (statusCheckIntervalRef.current) {
              clearInterval(statusCheckIntervalRef.current);
              statusCheckIntervalRef.current = null;
              console.log('🧹 Status check interval cleared on failure');
            }
            return; // Exit the function to prevent further checks
          }
        }
      } catch (error) {
        console.error('❌ Error checking payment status:', error);
        // Don't clear interval on error, just log it
      }
    };

    // Check status every 3 seconds
    console.log('🔄 Setting up interval for status check...');
    console.log('🔄 checkStatus function type:', typeof checkStatus);
    console.log('🔄 checkStatus function exists:', !!checkStatus);
    
    statusCheckIntervalRef.current = setInterval(checkStatus, 3000);
    console.log('🔄 Status check interval started with ID:', statusCheckIntervalRef.current);
    console.log('🔄 Status check will run every 3 seconds for transaction:', currentTransactionIdRef.current);
    console.log('🔄 Interval setup completed at:', new Date().toISOString());
    
    // Run initial check immediately
    console.log('🔄 Running initial status check immediately...');
    try {
      console.log('🔄 About to call checkStatus function');
      checkStatus();
      console.log('🔄 Initial status check called successfully');
    } catch (error: any) {
      console.error('❌ Error calling initial status check:', error);
      console.error('❌ Error details:', error.message, error.stack);
    }
    
    // Clear interval after 15 minutes as a safety measure
    setTimeout(() => {
      console.log('⏰ Payment status check timeout reached, clearing interval');
      isStatusCheckActiveRef.current = false;
      currentTransactionIdRef.current = null;
      if (statusCheckIntervalRef.current) {
        clearInterval(statusCheckIntervalRef.current);
        statusCheckIntervalRef.current = null;
        console.log('🧹 Status check interval cleared on timeout');
      }
    }, 900000);
  }, [navigate, paymentData, paymentResponse]);

  // Timer countdown
  useEffect(() => {
    if (status === 'success' && timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(timeRemaining - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [status, timeRemaining]);

  // Cleanup effect to clear interval when component unmounts
  useEffect(() => {
    return () => {
      console.log('🧹 Component unmounting, clearing status check interval');
      isStatusCheckActiveRef.current = false;
      currentTransactionIdRef.current = null;
      if (statusCheckIntervalRef.current) {
        clearInterval(statusCheckIntervalRef.current);
        statusCheckIntervalRef.current = null;
      }
    };
  }, []);

  // Clear interval when status changes to success or failed
  useEffect(() => {
    if (status === 'success' || status === 'failed') {
      console.log('🧹 Status changed to', status, ', clearing status check interval');
      isStatusCheckActiveRef.current = false;
      currentTransactionIdRef.current = null;
      if (statusCheckIntervalRef.current) {
        clearInterval(statusCheckIntervalRef.current);
        statusCheckIntervalRef.current = null;
      }
    }
  }, [status]);

  // Generate QR code when payment response is available
  useEffect(() => {
    console.log('🔍 Payment response changed:', paymentResponse);
    
    // Try multiple possible paths for payment link
    const paymentLink = paymentResponse?.order?.payment_link || 
                       paymentResponse?.data?.order?.payment_link ||
                       paymentResponse?.data?.payment_link ||
                       paymentResponse?.payment_link;
    
    if (paymentLink) {
      console.log('🔍 Generating QR code for payment link:', paymentLink);
      generateQRCode(paymentLink);
    } else {
      console.log('⚠️ No payment link found in response structure:', {
        hasData: !!paymentResponse?.data,
        hasOrder: !!paymentResponse?.data?.order,
        hasPaymentLink: !!paymentResponse?.data?.order?.payment_link,
        hasDirectOrder: !!paymentResponse?.order,
        hasDirectPaymentLink: !!paymentResponse?.order?.payment_link,
        hasDataPaymentLink: !!paymentResponse?.data?.payment_link,
        hasPaymentLinkDirect: !!paymentResponse?.payment_link,
        fullResponse: paymentResponse
      });
    }
  }, [paymentResponse]);

  useEffect(() => {
    // Process payment using real API
    const processPayment = async () => {
      // Prevent multiple API calls
      if (hasCreatedOrderRef.current) {
        console.log('🛑 Payment order already created, skipping duplicate call');
        return;
      }
      if (isApiCallInProgressRef.current) {
        console.log('🛑 API call already in progress, skipping duplicate call');
        return;
      }
      
      isApiCallInProgressRef.current = true;
      
      try {
        // setLoading(true); // Loading state not used in UI
        setStatus('processing');
        
        console.log('🔍 Processing payment with data:', paymentData);
        console.log('🔍 Customer data:', paymentData?.customerData);
        console.log('🔍 Amount data:', paymentData?.amountData);
        console.log('🔍 Gateway:', paymentData?.gateway);
        
        // Create final payment data with fallbacks for missing properties
        const finalPaymentData = {
          gateway: paymentData?.gateway || 'sahkar_pg',
          customerData: paymentData?.customerData || {
            fullName: 'Test Devotee',
            mobileNumber: '9999999999'
          },
          amountData: paymentData?.amountData || {
            amount: 10,
            quantity: 0.001
          },
          ...paymentData // Spread any additional properties
        };
        
        if (!paymentData) {
          console.warn('⚠️ No payment data found, using default test data');
        } else if (!paymentData.gateway) {
          console.warn('⚠️ No gateway found in payment data, using default: sahkar_pg');
        }
        
        // Get logged-in user information
        const loggedInUser = getCurrentUser();
        console.log('🔍 Logged-in user:', loggedInUser);
        
        // Validate required data - check multiple possible data structures
        const customerName = finalPaymentData.customerData?.fullName || 
                           finalPaymentData.customerData?.devotee_name || 
                           finalPaymentData.devotee_name ||
                           'Test Devotee';
        const customerMobile = finalPaymentData.customerData?.mobileNumber || 
                             finalPaymentData.customerData?.devotee_phone || 
                             finalPaymentData.devotee_phone ||
                             '9999999999';
        
        console.log('🔍 Validation check:', { 
          customerName, 
          customerMobile, 
          customerData: finalPaymentData.customerData,
          fullPaymentData: finalPaymentData 
        });
        
        if (!customerName || !customerMobile) {
          console.error('❌ Missing devotee info:', { customerName, customerMobile, customerData: finalPaymentData.customerData });
          throw new Error('Missing devotee information. Please go back and complete the verification.');
        }
        
        const amount = finalPaymentData.amountData?.amount || 
                      finalPaymentData.amountData?.amount_inr || 
                      finalPaymentData.amount ||
                      10; // Default amount for testing
        
        console.log('🔍 Amount validation:', { amount, amountData: finalPaymentData.amountData });
        
        if (!amount || amount < 10) {
          console.error('❌ Invalid amount:', { amount, amountData: finalPaymentData.amountData });
          throw new Error('Invalid donation amount. Please go back and enter a valid amount.');
        }
        
        if (!loggedInUser) {
          console.warn('⚠️ No logged-in user found, using default values');
          // Don't throw error, just use default values
        }
        
        // Prepare payment order data
        const orderData = {
          // Logged-in user information (who is processing the payment)
          user_name: loggedInUser?.user_name || 'Test User',
          user_mobile: loggedInUser?.mobile_number || '9999999999',
          user_email: loggedInUser?.email_id || 'test@example.com',
          // Devotee information (who is making the donation)
          devotee_name: customerName,
          devotee_phone: customerMobile,
          // Payment details
          amount_inr: amount,
          metal_type: 'gold',
          quantity_grams: finalPaymentData.amountData?.quantity || 0
        };

        console.log('📝 Sending payment order:', orderData);
        console.log('📝 API URL:', `${API_BASE_URL}/payments/create-order/${finalPaymentData.gateway}`);
        
        // Create payment order using the selected gateway
        hasCreatedOrderRef.current = true;
        const response = await apiService.post(`/payments/create-order/${finalPaymentData.gateway}`, orderData);
        
        console.log('📊 Payment creation response:', response);
        console.log('📊 Payment creation response structure:', {
          success: response.success,
          hasData: !!response.data,
          dataKeys: response.data ? Object.keys(response.data) : [],
          hasTransaction: !!response.data?.transaction,
          transactionKeys: response.data?.transaction ? Object.keys(response.data.transaction) : [],
          fullResponse: JSON.stringify(response, null, 2)
        });
        
        if (response.success) {
          setStatus('success');
          // setLoading(false); // Loading state not used in UI
          
          // Store the payment response data for display
          setPaymentResponse(response.data);
          
          // Start payment status checking only if we have a valid transaction ID
          // Note: apiService.post already returns response.data, so we don't need response.data.data
          const transactionId = response.data?.transaction?.id || 
                               response.transaction?.id ||
                               response.data?.id;
          console.log('🔍 Extracted transaction ID:', transactionId);
          console.log('🔍 Response structure for debugging:', {
            'response.data': response.data,
            'response.data.transaction': response.data?.transaction,
            'response.data.transaction.id': response.data?.transaction?.id,
            'response.transaction': response.transaction,
            'response.transaction.id': response.transaction?.id,
            'response.data.id': response.data?.id
          });
          console.log('🔍 Full response structure:', {
            hasData: !!response.data,
            hasTransaction: !!response.data?.transaction,
            hasId: !!response.data?.transaction?.id,
            hasDirectTransaction: !!response.transaction,
            hasDirectId: !!response.transaction?.id,
            hasDataId: !!response.data?.id,
            responseKeys: Object.keys(response || {}),
            dataKeys: response.data ? Object.keys(response.data) : []
          });
          
          if (transactionId) {
            console.log('🔍 Starting payment status check for transaction:', transactionId);
            console.log('🔍 About to call startPaymentStatusCheck function');
            console.log('🔍 startPaymentStatusCheck function type:', typeof startPaymentStatusCheck);
            console.log('🔍 startPaymentStatusCheck function exists:', !!startPaymentStatusCheck);
            
            // Force call the function with a delay to ensure it's properly set up
            setTimeout(() => {
              try {
                console.log('🔍 About to call startPaymentStatusCheck with transactionId:', transactionId);
                startPaymentStatusCheck(transactionId);
                console.log('🔍 startPaymentStatusCheck function called successfully');
              } catch (error: any) {
                console.error('❌ Error calling startPaymentStatusCheck:', error);
                console.error('❌ Error stack:', error.stack);
              }
            }, 100); // 100ms delay
          } else {
            console.warn('⚠️ No transaction ID found in response, skipping status check');
            console.log('🔍 Full response data:', response.data);
            console.log('🔍 Available response paths:', {
              'response.data?.transaction?.id': response.data?.transaction?.id,
              'response.transaction?.id': response.transaction?.id,
              'response.data?.id': response.data?.id
            });
          }
          
        } else {
          throw new Error(response.error || 'Payment creation failed');
        }
        
      } catch (error: any) {
        console.error('❌ Payment processing error:', error);
        setStatus('failed');
        
        let errorMessage = t('pages.paymentProcessing.errors.paymentProcessingFailed') as string;
        if (error.message) {
          errorMessage = error.message;
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        setError(errorMessage);
        // setLoading(false); // Loading state not used in UI
      } finally {
        // Reset the API call flag
        isApiCallInProgressRef.current = false;
      }
    };

    if (paymentData) {
      processPayment();
    } else {
      setError(t('pages.paymentProcessing.errors.noPaymentData') as string);
      // setLoading(false); // Loading state not used in UI
    }
  }, [paymentData, navigate, startPaymentStatusCheck, t]);

  const handleRetryPayment = () => {
    navigate('/payment-gateway', { 
      state: { 
        customerData: paymentData?.customerData,
        amountData: paymentData?.amountData 
      } 
    });
  };

  const handleBackToAmount = () => {
    navigate('/enter-amount', { 
      state: { 
        customerData: paymentData?.customerData 
      } 
    });
  };

  if (!paymentData) {
    return (
      <div className="min-h-screen bg-black/20 backdrop-blur-sm flex items-center justify-center" style={{backgroundImage: 'url(/images/Asset5.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed'}}>
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">{t('pages.paymentProcessing.noPaymentDataFound')}</h2>
          <button
            onClick={() => navigate('/payment-gateway')}
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            {t('pages.paymentProcessing.goToPaymentGateway')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black/20 backdrop-blur-sm flex items-center justify-center" style={{backgroundImage: 'url(/images/Asset5.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed'}}>
      <div className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl mx-auto px-4 py-4 sm:py-6 md:py-8">
        {/* Language Switcher */}
        <div className="absolute top-4 right-4 z-10">
          <LanguageSwitcher />
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-400 to-orange-500 p-4 sm:p-6 text-center">
            <div className="flex items-center justify-center mb-3">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full border-2 border-white flex items-center justify-center">
                <img 
                  src="/images/Asset2.png" 
                  alt="Temple Logo" 
                  className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
                />
              </div>
            </div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-white">{t('pages.paymentProcessing.title')}</h1>
            <p className="text-white/90 text-xs sm:text-sm mt-1">{t('pages.paymentProcessing.subtitle')}</p>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Status Display */}
            <div className="text-center">
              {status === 'processing' && (
                <div className="space-y-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto">
                    <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-4 border-orange-500"></div>
                  </div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">{t('pages.paymentProcessing.processingPayment')}</h2>
                  <p className="text-sm sm:text-base text-gray-600">{t('pages.paymentProcessing.pleaseWait')}</p>
                </div>
              )}

              {status === 'success' && paymentResponse && (
                <div className="space-y-4 sm:space-y-6">

                  {/* Payment Details */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">{t('pages.paymentProcessing.paymentDetails')}</h3>
                    <div className="space-y-2 text-xs sm:text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('pages.paymentProcessing.amount')}:</span>
                        <span className="font-semibold text-gray-800">₹{paymentResponse.transaction?.amount_inr || paymentResponse.data?.transaction?.amount_inr || paymentData?.amountData?.amountInr || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('pages.paymentProcessing.goldQuantity')}:</span>
                        <span className="font-semibold text-gray-800">{paymentResponse.transaction?.metal_quantity || paymentResponse.data?.transaction?.metal_quantity || paymentData?.amountData?.quantity || 'N/A'}g</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('pages.paymentProcessing.rate')}:</span>
                        <span className="font-semibold text-gray-800">₹{paymentResponse.transaction?.rate_per_gram || paymentResponse.data?.transaction?.rate_per_gram || paymentData?.amountData?.ratePerGram || 'N/A'}/g</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('pages.paymentProcessing.devotee')}:</span>
                        <span className="font-semibold text-gray-800">{paymentResponse.transaction?.devotee_name || paymentResponse.data?.transaction?.devotee_name || paymentData?.customerData?.fullName || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('pages.paymentProcessing.orderId')}:</span>
                        <span className="font-semibold text-gray-800 text-xs">{paymentResponse.order?.order_id || paymentResponse.data?.order?.order_id || paymentResponse.transaction?.pg_payment_id || paymentResponse.data?.transaction?.pg_payment_id || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Timer */}
                  <div className="text-center">
                    <div className="text-3xl sm:text-4xl font-bold text-blue-600 mb-2">
                      {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
                    </div>
                    <p className="text-gray-600 text-xs sm:text-sm">{t('pages.paymentProcessing.timeRemaining')}</p>
                  </div>

                  {/* QR Code */}
                  <div className="text-center">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">{t('pages.paymentProcessing.scanQrCode')}</h3>
                    <div className="bg-white p-2 sm:p-4 rounded-lg border-2 border-gray-200 inline-block">
                      <canvas
                        ref={qrCodeRef}
                        className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 rounded"
                        style={{ display: 'block' }}
                      />
                    </div>
                    <p className="text-gray-600 text-xs sm:text-sm mt-2">{t('pages.paymentProcessing.sahkarPaymentGateway')}</p>
                  </div>

                  {/* Payment Status */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-3">
                      <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-blue-600"></div>
                      <div className="flex-1 text-center sm:text-left">
                        <p className="font-semibold text-blue-800 text-sm sm:text-base">{t('pages.paymentProcessing.waitingForPayment')}</p>
                        <p className="text-blue-600 text-xs sm:text-sm">{t('pages.paymentProcessing.completePaymentWithin', { time: `${Math.floor(timeRemaining / 60)}:${(timeRemaining % 60).toString().padStart(2, '0')}` })}</p>
                      </div>
                      <div className="flex items-center space-x-1 text-blue-600">
                        <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-xs sm:text-sm font-medium">{Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}</span>
                      </div>
                    </div>
                  </div>

                  {/* How to Pay */}
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">{t('pages.paymentProcessing.howToPay')}</h3>
                    <div className="space-y-2 sm:space-y-3">
                      <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
                        <h4 className="font-medium text-gray-800 mb-1 text-sm sm:text-base">{t('pages.paymentProcessing.option1ScanQr')}</h4>
                        <p className="text-gray-600 text-xs sm:text-sm">{t('pages.paymentProcessing.option1Description')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {status === 'failed' && (
                <div className="space-y-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 text-center">{t('pages.paymentProcessing.paymentFailed')}</h2>
                  <p className="text-sm sm:text-base text-gray-600 text-center">{error || t('pages.paymentProcessing.errors.somethingWentWrong')}</p>
                </div>
              )}
            </div>

            {/* Payment Details */}
            {paymentData && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">{t('pages.paymentProcessing.paymentDetails')}</h3>
                <div className="space-y-2 text-xs sm:text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('pages.paymentProcessing.amount')}:</span>
                    <span className="font-semibold text-gray-800">₹{paymentData.amountData?.amount?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('pages.paymentProcessing.gateway')}:</span>
                    <span className="font-semibold text-gray-800">{paymentData.gateway}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('pages.paymentProcessing.customer')}:</span>
                    <span className="font-semibold text-gray-800">{paymentData.customerData?.fullName}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {status === 'failed' && (
              <div className="space-y-2 sm:space-y-3">
                <button
                  onClick={handleRetryPayment}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 sm:py-3 px-4 rounded-lg transition-colors text-sm sm:text-base"
                >
                  {t('pages.paymentProcessing.retryPayment')}
                </button>
                
                <button
                  onClick={handleBackToAmount}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 sm:py-3 px-4 rounded-lg transition-colors text-sm sm:text-base"
                >
                  {t('pages.paymentProcessing.backToAmount')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-4 sm:mt-6 md:mt-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {/* AUGMONT Logo */}
            <div className="bg-white rounded-lg px-3 sm:px-4 py-2 inline-block">
              <div className="flex flex-col items-center justify-center h-[88px] w-[240px]">
                <p className="text-xs sm:text-sm text-gray-700 mb-1 font-medium">Powered By</p>
                <div className="h-[72px] w-[240px] flex items-center justify-center mx-auto">
                  <img 
                    src="/images/Asset1.png" 
                    alt="AUGMONT GOLD" 
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </div>
            
            {/* Mandirsetu Initiative */}
            <div className="bg-white rounded-lg px-3 sm:px-4 py-2 inline-block">
              <div className="flex flex-col items-center justify-center h-[88px] w-[240px]">
                <p className="text-xs sm:text-sm text-gray-700 mb-1 font-medium">An Initiative By</p>
                <div className="h-[72px] w-[240px] flex items-center justify-center mx-auto">
                  <img 
                    src="/images/mandirSetu.png" 
                    alt="An Initiative By Mandirsetu" 
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentProcessingPage;

